import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap, List, Map } from 'immutable';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { canUseDOM } from 'exenv';

import Active from 'components/bookings/active';
import FrequentLocations from 'components/bookings/frequent-locations';
import LoadingPulse from 'components/common/loading-pulse';
import MobileAppBanner from 'components/common/ads/mobile-app-banner';
import NoBookingsPurchased from 'components/bookings/no-bookings-purchased';
import Past from 'components/bookings/past';
import TransferPassButton from 'components/bookings/transfer-pass-button';

import BookingsGroup from 'models/bookings-group';
import Brand from 'models/brand';
import PastBookings from 'models/past-bookings';

import addMessageAndScrollToTop from 'action-creators/messaging/add-message-and-scroll-to-top';
import getPastBookings from 'action-creators/bookings/get-past-bookings';
import getUpcomingBookings from 'action-creators/bookings/get-upcoming-bookings';
import reviewBooking from 'action-creators/bookings/review-booking';
import trackEventCreator from 'action-creators/analytics/track-event';

import RequestQueue from 'lib/api/request-queue';
import { PAGEVIEW_EVENT } from 'lib/analytics/events/index';
import { pageProps, urlProps } from 'lib/analytics/page-properties';

const propTypes = {
  addMessageAndScrollToTop: PropTypes.func.isRequired,
  app: PropTypes.string.isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  events: PropTypes.instanceOf(OrderedMap),
  frequentLocationBookings: PropTypes.instanceOf(OrderedMap),
  frequentLocations: PropTypes.instanceOf(List),
  getUpcomingBookings: PropTypes.func.isRequired,
  getPastBookings: PropTypes.func.isRequired,
  locations: PropTypes.instanceOf(OrderedMap),
  pastBookings: PropTypes.instanceOf(PastBookings),
  requestQueue: PropTypes.instanceOf(RequestQueue),
  reviewBooking: PropTypes.func.isRequired,
  reviews: PropTypes.instanceOf(Map).isRequired,
  trackEvent: PropTypes.func.isRequired,
  upcomingBookings: PropTypes.instanceOf(BookingsGroup).isRequired,
};

const defaultProps = {
  events: OrderedMap(),
  frequentLocationBookings: OrderedMap(),
  frequentLocations: List(),
  locations: OrderedMap(),
  pastBookings: Map(),
  paymentMethods: OrderedMap(),
  requestQueue: new RequestQueue(),
};

class Bookings extends Component {
  componentWillMount() {
    if (canUseDOM) {
      const pageProperties = pageProps({ app: 'Bookings' });
      this.props.trackEvent({
        ...PAGEVIEW_EVENT,
        ...pageProperties,
        name: pageProperties.pageName,
        properties: urlProps(),
      });
    }
  }

  get noBookingsPurchased() {
    return this.noBookings && this.noBookingRequests;
  }

  get noBookings() {
    const { pastBookings: { allBookings, refundedBookings }, upcomingBookings } = this.props;

    return !allBookings.hasBookings && !refundedBookings.hasBookings && !upcomingBookings.bookings.size;
  }

  get noBookingRequests() {
    const queue = this.props.requestQueue;
    return !queue.length('bookings') && !queue.length('frequent_locations');
  }

  renderBookingHistory() {
    const { noBookingsPurchased } = this;
    const {
      locations,
      frequentLocations,
      frequentLocationBookings,
      brand,
      app,
      trackEvent,
      pastBookings,
      reviews,
    } = this.props;

    if (noBookingsPurchased) {
      return <NoBookingsPurchased brand={brand} />;
    }

    return (
      <div>
        <FrequentLocations
          locations={frequentLocations}
          recentBookings={frequentLocationBookings}
          brand={brand}
          app={app}
          trackEvent={trackEvent}
        />
        <Past
          locations={locations}
          fetchPastBookings={this.props.getPastBookings}
          brand={brand}
          reviews={reviews}
          reviewBooking={this.props.reviewBooking}
          app={app}
          trackEvent={trackEvent}
          allBookings={pastBookings.allBookings}
          businessBookings={pastBookings.businessBookings}
          refundedBookings={pastBookings.refundedBookings}
        />
      </div>
    );
  }

  render() {
    const { noBookings, noBookingRequests } = this;
    const { locations, events, brand, app, trackEvent, upcomingBookings } = this.props;

    if (noBookings && !noBookingRequests) {
      return (
        <div className="vertical-padding-top-2x vertical-padding-bottom-2x visible-xs visible-sm">
          <LoadingPulse className="center-block" visible />
        </div>
      );
    }

    return (
      <div>
        <TransferPassButton brand={brand} app={app} trackEvent={trackEvent} />
        <Active
          upcomingBookings={upcomingBookings}
          locations={locations}
          events={events}
          brand={brand}
          addMessageAndScrollToTop={this.props.addMessageAndScrollToTop}
          app={app}
          trackEvent={trackEvent}
          getBookings={this.props.getUpcomingBookings}
        />
        { this.renderBookingHistory() }
        <MobileAppBanner />
      </div>
    );
  }
}

Bookings.propTypes = propTypes;
Bookings.defaultProps = defaultProps;

const mapStateToProps = state => ({
  app: state.app.name,
  brand: state.brand.brand,
  events: state.bookings.events,
  frequentLocationBookings: state.bookings.frequentLocationBookings,
  frequentLocations: state.bookings.frequentLocations,
  locations: state.bookings.locations,
  pastBookings: state.bookings.pastBookings,
  paymentMethods: state.paymentMethods.paymentMethods,
  requestQueue: state.router.requestQueue,
  reviews: state.bookings.reviews,
  upcomingBookings: state.bookings.upcomingBookings,
});

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    getUpcomingBookings,
    getPastBookings,
    reviewBooking,
    trackEvent: trackEventCreator,
    addMessageAndScrollToTop,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(Bookings);
